/** Indian identity and tax number validators (ported from the prototype's core/validators.py, plus GSTIN/IFSC). */

// Verhoeff tables for the Aadhaar check digit.
const D = [[0, 1, 2, 3, 4, 5, 6, 7, 8, 9], [1, 2, 3, 4, 0, 6, 7, 8, 9, 5], [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
  [3, 4, 0, 1, 2, 8, 9, 5, 6, 7], [4, 0, 1, 2, 3, 9, 5, 6, 7, 8], [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2], [7, 6, 5, 9, 8, 2, 1, 0, 4, 3], [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
  [9, 8, 7, 6, 5, 4, 3, 2, 1, 0]];
const P = [[0, 1, 2, 3, 4, 5, 6, 7, 8, 9], [1, 5, 7, 6, 2, 8, 3, 0, 9, 4], [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
  [8, 9, 1, 6, 0, 4, 3, 5, 2, 7], [9, 4, 5, 3, 1, 2, 6, 8, 7, 0], [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5], [7, 0, 4, 6, 9, 1, 3, 2, 5, 8]];

export function verhoeffOk(number: string): boolean {
  let c = 0;
  const digits = number.split("").reverse();
  for (let i = 0; i < digits.length; i++) {
    const row = D[c];
    const perm = P[i % 8];
    if (!row || !perm) return false;
    c = row[perm[Number(digits[i])] ?? 0] ?? 0;
  }
  return c === 0;
}

export const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
export const AADHAAR_RE = /^[2-9][0-9]{11}$/;
export const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
export const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
export const PHONE_RE = /^[6-9][0-9]{9}$/;
export const PINCODE_RE = /^[1-9][0-9]{5}$/;

export const isPAN = (v: string): boolean => PAN_RE.test(v.trim().toUpperCase());
export const isAadhaar = (v: string): boolean => AADHAAR_RE.test(v.replace(/\s/g, "")) && verhoeffOk(v.replace(/\s/g, ""));
export const isIFSC = (v: string): boolean => IFSC_RE.test(v.trim().toUpperCase());
export const isPhone = (v: string): boolean => PHONE_RE.test(normalisePhone(v));

/** GSTIN check digit (mod 36, weights alternate 1 and 2). */
export function isGSTIN(value: string): boolean {
  const v = value.trim().toUpperCase();
  if (!GSTIN_RE.test(v)) return false;
  const chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  let sum = 0;
  for (let i = 0; i < 14; i++) {
    const code = chars.indexOf(v.charAt(i)) * (i % 2 === 0 ? 1 : 2);
    sum += Math.floor(code / 36) + (code % 36);
  }
  return chars.charAt((36 - (sum % 36)) % 36) === v.charAt(14);
}

/** PAN 4th letter: P individual, C company, H HUF, F firm, A AOP, T trust, B BOI, L local authority, J juridical, G government. */
export const panHolderType = (pan: string): string | undefined =>
  ({ P: "individual", C: "company", H: "huf", F: "firm", A: "aop", T: "trust", B: "boi", L: "local_authority", J: "juridical", G: "government" })[
    pan.charAt(3).toUpperCase()
  ];

/** "+91 98765-43210" -> "9876543210" */
export function normalisePhone(v: string): string {
  const digits = v.replace(/\D/g, "");
  return digits.length === 12 && digits.startsWith("91") ? digits.slice(2) : digits;
}

export function maskPhone(v: string | null | undefined): string {
  if (!v) return "";
  const p = normalisePhone(v);
  return p.length === 10 ? `${p.slice(0, 2)}XXXXXX${p.slice(-2)}` : "XXXXXXXX";
}
